/**
 * JobStatusCell - Displays job status icon with animation for running jobs
 *
 * Extracted from Jobs.tsx (lines 361-407) for modularity and reusability
 */

import {
  ArrowPathIcon,
  CheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { HourglassIcon } from 'lucide-react';
import { memo } from 'react';

import { Tooltip } from '@/components/Tooltip';
import { SPINNER_MOTION } from '@/components/ui/spinner';
import { JobStatusCellProps } from '@/sections/jobs/types';
import { JobStatus } from '@/types';
import { cn } from '@/utils/classname';
import { getJobStatus } from '@/utils/job';

/**
 * Get status icon component for job status
 *
 * @param status - Job status enum value
 * @param animateRunning - Whether to animate running status icon
 * @returns React element for status icon
 */
// eslint-disable-next-line react-refresh/only-export-components -- pre-existing; out of scope for loading-spinner standardization
export const getStatusIcon = (status: JobStatus, animateRunning = true) => {
  switch (status) {
    case JobStatus.Running:
    case JobStatus.AsyncRunning:
      return (
        <ArrowPathIcon
          data-testid={
            status === JobStatus.AsyncRunning
              ? 'job-status-async-running'
              : 'job-status-running'
          }
          className={cn(
            'text-chariot-text-primary size-5',
            animateRunning && SPINNER_MOTION
          )}
        />
      );
    case JobStatus.Queued:
    case JobStatus.AsyncReturning:
      return (
        <HourglassIcon
          data-testid={
            status === JobStatus.AsyncReturning
              ? 'job-status-async-returning'
              : 'job-status-queued'
          }
          className="text-chariot-text-secondary size-5"
        />
      );
    case JobStatus.Pass:
      return (
        <CheckIcon
          data-testid="job-status-pass"
          className="size-5 text-[#10B981]"
        />
      );
    case JobStatus.Fail:
      return (
        <XMarkIcon
          data-testid="job-status-fail"
          className="text-chariot-stroke-critical size-5"
        />
      );
    default:
      return null;
  }
};

/**
 * JobStatusCell component
 *
 * Renders job status with appropriate icon and animation.
 * Running jobs show spinning arrow, queued show hourglass,
 * completed show checkmark, failed show X.
 * Failed jobs show a tooltip with the job's comment if available.
 */
export const JobStatusCell = memo(
  ({ job, animateRunning = true }: JobStatusCellProps) => {
    const status = getJobStatus(job);
    const icon = getStatusIcon(status, animateRunning);

    // Wrap failed jobs with tooltip if comment exists
    if (status === JobStatus.Fail && job.comment) {
      return (
        <Tooltip title={job.comment} placement="top">
          <div>{icon}</div>
        </Tooltip>
      );
    }

    return <>{icon}</>;
  }
);

JobStatusCell.displayName = 'JobStatusCell';
